import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useConvertRewards,
  usePreviewConvertRewards,
} from "@/api/hooks/use-query-api";
import { useDebounce } from "@/hooks/use-debounce";
import { useToast } from "@/features/toast/hooks/use-toast";
import type {
  CosmopointsConnection,
  RewardConversionTarget,
} from "@/api/models/api-types";
import { ProgramLogo } from "./components/ProgramLogo";
import { useCosmopointsEnabled } from "./hooks/use-cosmopoints-enabled";

interface ConvertRewardsDialogProps {
  open: boolean;
  onClose: () => void;
  availableCredits: number;
  /**
   * When set, credits convert into this program's points instead of Harmony
   * credits. The caller picks the program — either from the overview button
   * (none) or from a linked program row — so there is nothing to choose here.
   */
  connection?: CosmopointsConnection;
}

export const ConvertRewardsDialog: React.FC<ConvertRewardsDialogProps> = ({
  open,
  onClose,
  availableCredits,
  connection,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { mutateAsync: convertRewards, isPending } = useConvertRewards();

  // The deployment can stop issuing partner points while a dialog is open, or
  // the site-configs cache can be stale — never send a request the API will
  // reject.
  const { isEnabled: isCosmopointsEnabled } = useCosmopointsEnabled();

  const isPointsMode = Boolean(connection);
  const target: RewardConversionTarget = isPointsMode ? "cosmopoints" : "tokens";

  const schema = z.object({
    amount: z
      .number({ message: t("settings.rewards.errors.min") })
      .int(t("settings.rewards.errors.min"))
      .positive(t("settings.rewards.errors.min"))
      .max(availableCredits, t("settings.rewards.errors.exceeds")),
  });

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { amount: undefined },
  });

  const amount = watch("amount");
  // The points leg hits the partner program live, so don't quote on every
  // keystroke.
  const debouncedAmount = useDebounce(amount, 400);
  const quotedAmount =
    Number.isFinite(debouncedAmount) && debouncedAmount > 0
      ? debouncedAmount
      : 0;

  const { data: previewData, isFetching: isPreviewLoading } =
    usePreviewConvertRewards(
      quotedAmount,
      connection?._id,
      open && isPointsMode && isCosmopointsEnabled
    );

  const quote = previewData?.data?.cosmopoints ?? null;
  const unavailableReason = previewData?.data?.unavailableReason;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    try {
      const result = await convertRewards({
        amount: data.amount,
        target,
        connectionId: connection?._id,
      });

      if (isPointsMode) {
        success(
          t("settings.rewards.cosmopoints.success", {
            points: result.data?.totalPoints ?? 0,
            program:
              result.data?.programName ??
              connection?.programName ??
              t("settings.rewards.cosmopoints.name"),
          })
        );
      } else {
        success(t("settings.rewards.success", { count: data.amount }));
      }
      handleClose();
    } catch {
      showError(
        isPointsMode
          ? t("settings.rewards.cosmopoints.error")
          : t("settings.rewards.error")
      );
    }
  };

  if (!open) return null;

  // A quote that came back unavailable means this amount won't go through
  // either, and points are off the table entirely once the flag is down.
  const pointsBlocked =
    isPointsMode &&
    (!isCosmopointsEnabled ||
      (quotedAmount > 0 && !quote && !isPreviewLoading));

  const submitDisabled = !isValid || isPending || pointsBlocked;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-gray-800 rounded-[20px] shadow-xl max-w-md w-full mx-4 max-h-[80vh] md:max-h-[90vh] overflow-y-auto mt-16 md:mt-0">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-zinc-800 dark:text-white">
              {t(
                isPointsMode
                  ? "settings.rewards.cosmopoints.dialogTitle"
                  : "settings.rewards.dialogTitle"
              )}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-zinc-800 dark:text-white" />
            </button>
          </div>

          {/* Available balance */}
          <p className="text-sm text-zinc-500 dark:text-gray-300">
            {t("settings.rewards.available", { count: availableCredits })}
          </p>

          {/* Destination program — fixed by whichever row opened the dialog */}
          {connection && (
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-border-secondary dark:border-gray-600 p-3">
              <ProgramLogo src={connection.programLogo} />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="truncate text-sm font-semibold text-text-primary dark:text-gray-100">
                  {connection.programName ?? connection.programId}
                </p>
                <p className="truncate text-sm text-text-quaternary dark:text-gray-400">
                  {connection.destinationAccount}
                </p>
              </div>
            </div>
          )}

          {/* Amount input */}
          <div className="space-y-2">
            <label
              htmlFor="amount"
              className="block text-sm font-semibold text-zinc-700 dark:text-gray-300"
            >
              {t("settings.rewards.amountLabel")}
            </label>
            <Input
              id="amount"
              type="number"
              min={1}
              max={availableCredits}
              step={1}
              placeholder="0"
              aria-invalid={!!errors.amount}
              {...register("amount", { valueAsNumber: true })}
            />
            {errors.amount ? (
              <p className="text-sm text-destructive">{errors.amount.message}</p>
            ) : (
              <ConversionHint
                target={target}
                amount={Number.isFinite(amount) && amount > 0 ? amount : 0}
                isLoading={isPreviewLoading}
                totalPoints={quote?.totalPoints}
                programName={quote?.programName ?? connection?.programName}
                unavailableReason={unavailableReason}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-3.5 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("settings.rewards.cancel")}
            </button>
            <Button
              type="submit"
              variant="darkPrimary"
              disabled={submitDisabled}
              className="px-4 py-2.5 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? t("common.loading") + "..."
                : t("settings.rewards.confirm")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ConversionHintProps {
  target: RewardConversionTarget;
  amount: number;
  isLoading: boolean;
  totalPoints?: number;
  programName?: string;
  unavailableReason?: string;
}

/** The "you will receive …" line under the amount field. */
const ConversionHint: React.FC<ConversionHintProps> = ({
  target,
  amount,
  isLoading,
  totalPoints,
  programName,
  unavailableReason,
}) => {
  const { t } = useTranslation();

  if (target === "tokens") {
    return (
      <p className="text-xs text-text-quaternary dark:text-gray-400">
        {t("settings.rewards.youWillReceive", { count: amount })}
      </p>
    );
  }

  if (amount <= 0) {
    return (
      <p className="text-xs text-text-quaternary dark:text-gray-400">
        {t("settings.rewards.cosmopoints.enterAmount")}
      </p>
    );
  }

  if (isLoading) {
    return (
      <p className="text-xs text-text-quaternary dark:text-gray-400">
        {t("settings.rewards.cosmopoints.calculating")}
      </p>
    );
  }

  if (unavailableReason) {
    // Reason codes mirror errors.cosmopoints.* on the API; fall back to a
    // generic line for any code the UI doesn't have copy for yet.
    return (
      <p className="text-xs text-destructive">
        {t(
          [
            `settings.rewards.cosmopoints.reasons.${unavailableReason}`,
            "settings.rewards.cosmopoints.reasons.generic",
          ] as unknown as string
        )}
      </p>
    );
  }

  if (totalPoints === undefined) return null;

  return (
    <p className="text-xs text-text-quaternary dark:text-gray-400">
      {t("settings.rewards.cosmopoints.youWillReceive", {
        points: totalPoints.toLocaleString(),
        program: programName ?? t("settings.rewards.cosmopoints.name"),
      })}
    </p>
  );
};
